import type { User } from "@supabase/supabase-js"

import { getClient, readAuthSession } from "../utils/supabase"
import type Authentication from "../types/authentication"

function pickNonEmptyString(v: unknown): string | undefined {
  return typeof v === "string" && v.trim().length > 0 ? v.trim() : undefined
}

export function profileFieldsFromUserMetadata(
  meta: Record<string, unknown> | undefined,
): Pick<Authentication, "givenName" | "profileDisplayName"> {
  return {
    givenName: pickNonEmptyString(meta?.given_name),
    profileDisplayName:
      pickNonEmptyString(meta?.full_name) ?? pickNonEmptyString(meta?.name),
  }
}

/** Google (and similar) expose `picture` / `avatar_url` on user_metadata or on the Google identity. */
export function oauthAvatarUrlFromUser(user: User | undefined): string | undefined {
  if (!user) return undefined
  const meta = user.user_metadata as Record<string, unknown> | undefined
  const fromMeta = pickNonEmptyString(meta?.avatar_url) ?? pickNonEmptyString(meta?.picture)
  if (fromMeta) return fromMeta

  const identities = user.identities
  if (!Array.isArray(identities)) return undefined
  for (const identity of identities) {
    if (identity.provider !== "google") continue
    const data = identity.identity_data as Record<string, unknown> | undefined
    const fromIdentity =
      pickNonEmptyString(data?.avatar_url) ?? pickNonEmptyString(data?.picture)
    if (fromIdentity) return fromIdentity
  }
  return undefined
}

function hasBackofficeAccess(roleTypes: Iterable<string>): boolean {
  for (const roleType of roleTypes) {
    if (roleType === "editor" || roleType === "super_admin") {
      return true
    }
  }
  return false
}

const AUTH_CHECK_TIMEOUT_MS = 12_000

function authenticationFromSession(
  session: Awaited<ReturnType<typeof readAuthSession>>["data"]["session"],
  isEditor = false,
): Authentication {
  const userId = session?.user?.id
  const meta = session?.user?.user_metadata as Record<string, unknown> | undefined
  const pathRaw = meta?.profile_avatar_object_path
  const profileAvatarObjectPath =
    typeof pathRaw === "string" && pathRaw.trim().length > 0 ? pathRaw.trim() : undefined
  const oauthAvatarUrl = oauthAvatarUrlFromUser(session?.user)
  const { givenName, profileDisplayName } = profileFieldsFromUserMetadata(meta)

  return {
    email: session?.user?.email,
    id: userId,
    isAnonymous: Boolean(session?.user?.is_anonymous),
    isAuthenticated: !!userId,
    isEditor,
    profileAvatarObjectPath,
    oauthAvatarUrl,
    givenName,
    profileDisplayName,
  } as Authentication
}

/** Post sign-in / sign-up — session only (no editor RPC; avoids 12s timeout on mobile). */
export async function getAuthenticationSessionOnly(): Promise<Authentication> {
  let session: Awaited<ReturnType<typeof readAuthSession>>["data"]["session"] = null
  try {
    ({
      data: { session },
    } = await _withAuthTimeout(readAuthSession(), "session"))
  } catch {
    session = null
  }
  return authenticationFromSession(session)
}

export async function getAuthentication(): Promise<Authentication> {
  let session: Awaited<ReturnType<typeof readAuthSession>>["data"]["session"] = null
  try {
    ({
      data: { session },
    } = await _withAuthTimeout(readAuthSession(), "session"))
  } catch {
    session = null
  }
  const userId = session?.user?.id

  let isEditor = false
  if (userId) {
    // Prefer SECURITY DEFINER RPC (bypasses nested RLS quirks on roles embed).
    try {
      const { data: roleTypes, error: rpcError } = await _withAuthTimeout(
        getClient().rpc("my_role_types"),
        "roles",
      )
      if (!rpcError && Array.isArray(roleTypes)) {
        isEditor = hasBackofficeAccess(roleTypes)
      } else {
        const { data } = await _withAuthTimeout(
          getClient()
            .from("accounts")
            .select("roles(type)")
            .eq("user_id", userId)
            .maybeSingle(),
          "accounts",
        )
        const roles = data?.roles
        const list = Array.isArray(roles) ? roles : roles ? [roles] : []
        isEditor = list.some(
          (r: { type: string }) => r.type === "editor" || r.type === "super_admin",
        )
      }
    } catch {
      isEditor = false
    }
  }

  return authenticationFromSession(session, isEditor)
}

export interface UseGetAuthenticationParams {
  redirectIfNotAuthenticated?: boolean
  redirectTo?: string
  requireEditor?: boolean
  redirectIfNotEditor?: string
}

async function _withAuthTimeout<T>(promise: PromiseLike<T>, label: string): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(
      () => reject(new Error(`Authentication ${label} check timed out`)),
      AUTH_CHECK_TIMEOUT_MS,
    )
  })
  try {
    return await Promise.race([Promise.resolve(promise), timeout])
  } finally {
    if (timer !== undefined) clearTimeout(timer)
  }
}
